import { observer } from 'mobx-react-lite';
import { useState, useEffect, useCallback } from 'react';
import { Input } from '@shared/components/common/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/common/Select';
import { Search } from 'lucide-react';
import { OrderCard } from './components';
import { useStore } from '@shared/hooks/useStore';
import Pagination from '@shared/components/pagination/Pagination';
import debounce from 'lodash.debounce';
import { ORDER_STATUS_LABELS } from '@shared/components/constants/select.constant';
import Calendar from '@shared/components/calendar/Calendar';
import { ORDER_STATUS } from '@/shared/types/enums';

const OrderStatusOptions = Object.entries(ORDER_STATUS_LABELS).map(([value, label]) => ({
    value,
    label
})).filter(item => item.value !== ORDER_STATUS.FAILURE.toString());

export default observer(function PurchaseHistoryPage() {
    const { orderStore } = useStore();
    const { orders, totalOrders } = orderStore;

    const [orderIdSearch, setOrderIdSearch] = useState('');
    const [orderDateSearch, setOrderDateSearch] = useState<{ from: Date, to: Date } | undefined>(undefined);
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    const fetchOrders = useCallback((page: number, code?: string, status?: string, orderDate?: { from: Date, to: Date }) => {
        const params: any = {
            page,
            size: pageSize
        };

        if (code) {
            params.code = code;
        }

        if (status && status !== 'all') {
            params.status = status;
        }

        if (orderDate) {
            params.order_at_from = orderDate.from.toISOString();
            const newToDate = new Date(orderDate.to);
            newToDate.setHours(23, 59, 59, 999);
            params.order_at_to = newToDate.toISOString();
        }

        orderStore.fetchOrderHistory(params);
    }, [orderStore]);

    // Debounced search to avoid too many API calls
    const debouncedFetch = useCallback(
        debounce((page, code, status, orderDate) => {
            fetchOrders(page, code, status, orderDate);
        }, 500),
        [fetchOrders]
    );

    // Initial fetch and when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [orderIdSearch, orderDateSearch, statusFilter]);

    useEffect(() => {
        debouncedFetch(
            currentPage,
            orderIdSearch || undefined,
            statusFilter !== 'all' ? statusFilter : undefined,
            orderDateSearch || undefined
        );
    }, [currentPage, orderIdSearch, orderDateSearch, statusFilter, debouncedFetch]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    return (
        <section className='space-y-6 w-full'>
            {/* Search and Filter Form */}
            <div className='space-y-6'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                    {/* Order ID Search */}
                    <div className='relative bg-white rounded-full'>
                        <Input
                            type='text'
                            placeholder='注文IDで検索'
                            value={orderIdSearch}
                            onChange={e => setOrderIdSearch(e.target.value)}
                            className='pr-10 pl-4 rounded-full min-h-12 text-base'
                        />
                        <Search className='absolute right-3 top-1/2 -translate-y-1/2 size-6 text-[#999] pointer-events-none' />
                    </div>

                    {/* Order Date Search */}
                    <div className=''>
                        <Calendar
                            mode='range'
                            required
                            placeholder='注文日で検索'
                            selected={orderDateSearch}
                            classButton='bg-white rounded-full w-full min-h-12 text-base'
                            onSelect={(range) => {
                                setOrderDateSearch(range as unknown as { from: Date, to: Date });
                            }}
                        />
                    </div>

                    {/* Status Filter */}
                </div>

                <div className='flex justify-end'>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className='w-full max-w-60 bg-white min-h-12 text-base'>
                            <SelectValue placeholder='ステータスで絞り込み' />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value='all'>すべて</SelectItem>
                            {OrderStatusOptions.map(item => (
                                <SelectItem key={item.value} value={item.value}>
                                    {item.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Order List */}
            <ul className='flex flex-col gap-3 md:gap-6'>
                {orders.length > 0 ?
                    (
                        orders.map(order => (
                            <li key={order.id} className='w-full animate-in fade-in'>
                                <OrderCard order={order} />
                            </li>
                        ))
                    ) :
                    (
                        <div className='bg-white rounded-lg shadow-md p-6 text-center'>
                            <p className='text-gray-600'>該当する注文が見つかりませんでした。</p>
                        </div>
                    )}
            </ul>

            {/* Pagination */}
            {totalOrders > 0 && (
                <div className='flex justify-center mt-6'>
                    <Pagination
                        type='full-pagination'
                        paginationClassName='w-fit'
                        currentPage={currentPage}
                        totalRecords={totalOrders}
                        pageSize={pageSize}
                        onPageChange={handlePageChange}
                    />
                </div>
            )}
        </section>
    );
});
